import axiosInstance from "@/config/axios";
import type {
	CreateFinancialRequest,
} from "@/types/finance";
import type { CreateDebtRequest, CreateCashRequest, FixAssetDetail, CreatePropertyRequest } from "@/types/finance";

const getFinance = async (
	params: Record<string, string | number | undefined>,
) => {
	if (!params.companyId) throw new Error("CompanyId is required");

	const cleanedId = String(params.companyId).replace(/^"(.+)"$/, "$1");

	try {
		const response = await axiosInstance.get("/reports/company-financial", {
			params: {
				companyId: cleanedId,
				...params,
			},
		});
		return response.data;
	} catch (error) {
		console.error("Error fetching finance data:", error);
		throw error;
	}
};
const getResult = async (params: {
	companyId: string;
	yearly: number;
}) => {
	if (!params.companyId) throw new Error("CompanyId is required");

	const cleanedId = params.companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,

			...(params.yearly !== 1111 && { yearly: params.yearly }),
		},
	};

	try {
		const response = await axiosInstance.get("/financial", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching finance data:", error);
		throw error;
	}
};
const lastgetResult = async (params: {
	companyId: string;
	yearly: number;
}) => {
	if (!params.companyId) throw new Error("CompanyId is required");

	const cleanedId = params.companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,

			...(params.yearly !== 1111 && { yearly: params.yearly }),
		},
	};

	try {
		const response = await axiosInstance.get("/financial", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching finance data:", error);
		throw error;
	}
};

const getProperty = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/assets", configs);
		return response.data.data; // Adjusted to return the nested data
	} catch (error) {
		console.error("Error fetching property data:", error);
		throw error;
	}
};
const lastgetProperty = async (companyId: string, yearly: number) => {
	console.log("companyId", companyId, "year", yearly);

	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/assets", configs);
		return response.data.data; // Adjusted to return the nested data
	} catch (error) {
		console.error("Error fetching property data:", error);
		throw error;
	}
};

const getFixAssets = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
			limit: 10,
			skip: 0,
		},
	};
	try {
		const response = await axiosInstance.get("/fix-assets", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching fix assets data:", error);
		throw error;
	}
};

const getDebt = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/debts", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching debts data:", error);
		throw error;
	}
};
const lastgetDebt = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/debts", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching debts data:", error);
		throw error;
	}
};

const getCash = async (companyId: string, yearly: number, cashType: string) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
			cashType,
		},
	};
	try {
		const response = await axiosInstance.get("/cashes", configs);
		return response.data;
	} catch (error) {
		console.error("Error fetching cash data:", error);
		throw error;
	}
};

const getEquities = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/equities", configs);
		return response.data; // Adjusted to return the nested equityDetails
	} catch (error) {
		console.error("Error fetching equities data:", error);
		throw error;
	}
};

const editFinance = async (
	companyId: string,
	data: {
		companyId: string;
		yearly: number;
		docFile: string;
		assetDetails: {
			assetType: string;
			name: string;
			amount: number;
			remark: string;
		}[];
	},
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/assets", data);
		return response.data;
	} catch (error) {
		console.error("Error editing finance data:", error);
		throw error;
	}
};

const editDebt = async (
	companyId: string,
	data: {
		companyId: string;
		yearly: number;
		docFile: string;
		debtDetails: {
			debtType: string;
			name: string;
			amount: number;
			remark: string;
		}[];
	},
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/debts", data);
		return response.data;
	} catch (error) {
		console.error("Error editing debt data:", error);
		throw error;
	}
};

const editCashes = async (
	companyId: string,
	data: {
		companyId: string;
		yearly: number;
		docFile: string;
		cashDetails: {
			cashType: string;
			name: string;
			amount: number;
			remark: string;
		}[];
	},
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/cashes", data);
		return response.data;
	} catch (error) {
		console.error("Error editing cash data:", error);
		throw error;
	}
};

const editEquities = async (
	companyId: string,
	data: {
		companyId: string;
		yearly: number;
		docFile: string;
		equityDetails: {
			id: string;
			name: string;
			remark: string | null;
			registeredCapital: number | null;
			accProfitLoss: number | null;
			reserve: number;
			other: number;
		}[];
	},
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/equities", data);
		return response.data;
	} catch (error) {
		console.error("Error editing equities data:", error);
		throw error;
	}
};

const editResult = async (data: CreateFinancialRequest) => {
	try {
		const response = await axiosInstance.put("/financial", data);
		return response.data;
	} catch (error) {
		console.error("Error editing equities data:", error);
		throw error;
	}
};

const postProperty = async (data: CreatePropertyRequest) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");
		const requestData = {
			...data,
			companyId: cleanedId,
		};

		const response = await axiosInstance.post("/assets", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating property data:", error);
		throw error;
	}
};

const postDebt = async (data: CreateDebtRequest) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");
		const requestData = {
			...data,
			companyId: cleanedId,
		};

		const response = await axiosInstance.post("/debts", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating debt data:", error);
		throw error;
	}
};

const postCash = async (data: CreateCashRequest) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");
		const requestData = {
			...data,
			companyId: cleanedId,
		};

		const response = await axiosInstance.post("/cashes", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating cash data:", error);
		throw error;
	}
};

const postResult = async (data: CreateFinancialRequest) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");

		// Transform financialDetails to use absolute amounts
		const transformedDetails = data.financialDetails.map((detail) => ({
			...detail,
			amount: Math.abs(detail.amount),
		}));

		const requestData = {
			...data,
			companyId: cleanedId,
			financialDetails: transformedDetails,
		};

		const response = await axiosInstance.post("/financial", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating financial data:", error);
		throw error;
	}
};

const postEquity = async (data: any) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");
		const requestData = {
			...data,
			companyId: cleanedId,
		};

		const response = await axiosInstance.post("/equities", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating equity data:", error);
		throw error;
	}
};

// Fix Assest: ຊັບສິນຄົງທີ່
const getFixAsset = async (companyId: string, yearly: number) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	const configs = {
		params: {
			companyId: cleanedId,
			yearly,
		},
	};
	try {
		const response = await axiosInstance.get("/fix-assets", configs);
		return response.data; // Adjusted to return the nested equityDetails
	} catch (error) {
		console.error("Error fetching equities data:", error);
		throw error;
	}
};

const postFixAsset = async (data: any) => {
	try {
		const cleanedId = data.companyId.replace(/^"(.+)"$/, "$1");
		const requestData = {
			...data,
			companyId: cleanedId,
		};

		const response = await axiosInstance.post("/fix-assets", requestData);
		return response.data;
	} catch (error) {
		console.error("Error creating equity data:", error);
		throw error;
	}
};

const editFixAssets = async (
	companyId: string,
	data: {
		companyId?: string;
		yearly: number;
		docFile: string;
		fixAssetDetails: FixAssetDetail[];
	},
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/fix-assets", data);
		return response.data;
	} catch (error) {
		console.error("Error editing equities data:", error);
		throw error;
	}
};

// ຕົວຊີ້ວັດທີ່ຈະນຳໃຊ້ຂໍ້ມູນ
export const getIndicators = async (params?: {
	[key: string]: string | number;
}) => {
	const { data } = await axiosInstance.get("/indicators", { params });
	return data;
};

export const createIndicator = async (payload?: {
	[key: string]: string | number;
}) => {
	const { data } = await axiosInstance.post("/indicators", payload);
	return data;
};

export const editIndicator = async (
	companyId: string,
	data: { [key: string]: string | number | undefined },
) => {
	const cleanedId = companyId.replace(/^"(.+)"$/, "$1");
	data.companyId = cleanedId;
	try {
		const response = await axiosInstance.put("/indicators", data);
		return response.data;
	} catch (error) {
		console.error("Error editing equities data:", error);
		throw error;
	}
};

export {
	getFinance,
	getProperty,
	editFinance,
	getFixAssets,
	getDebt,
	editDebt,
	getCash,
	getEquities,
	editCashes,
	editEquities,
	getResult,
	editResult,
	postProperty,
	postDebt,
	postCash,
	postResult,
	postEquity,
	lastgetProperty,
	lastgetDebt,
	lastgetResult,
	getFixAsset,
	postFixAsset,
	editFixAssets,
};
